import type { LinkGrantVerifyResult } from '@/api/types'

/**
 * The scoped access token `verify` minted for one link, kept for the life of the tab.
 *
 * A scan link's grant is repeatable - a None factor never burns it - but every `verify` against it
 * is one of the five an hour Auth's handshake allows, and this app was spending one per page load.
 * Five reloads locked a patient out of a link that was never wrong. So the token is verified ONCE
 * and remembered here; a remount or a reload of the same tab picks it up and calls Auth for
 * nothing (docs/decisions.md D-45).
 *
 * `sessionStorage`, not `localStorage`: the tab is the session. A second tab, or the same link
 * tomorrow, verifies again, which is what "once per link" is allowed to cost. Every access is
 * guarded because storage is not promised - a private window, a browser set to block site data -
 * and a page that cannot remember must still work, at the price it paid before.
 */
export function readScanSession(token: string, now: number = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(keyFor(token))
    if (raw === null) return null
    const session = JSON.parse(raw) as StoredScanSession
    if (typeof session.accessToken !== 'string' || typeof session.expiresAt !== 'number') return null
    return session.expiresAt > now ? session.accessToken : null
  } catch {
    return null
  }
}

export function storeScanSession(token: string, verified: LinkGrantVerifyResult, now: number = Date.now()): void {
  const session: StoredScanSession = {
    accessToken: verified.accessToken,
    expiresAt: now + verified.expiresInSeconds * 1000,
  }
  try {
    sessionStorage.setItem(keyFor(token), JSON.stringify(session))
  } catch {
    // The page keeps working without the memory; it verifies again next time.
  }
}

export function forgetScanSession(token: string): void {
  try {
    sessionStorage.removeItem(keyFor(token))
  } catch {
    // Nothing was stored, or nothing can be.
  }
}

interface StoredScanSession {
  readonly accessToken: string
  readonly expiresAt: number
}

function keyFor(token: string): string {
  return `scan-session:${token}`
}
